import { css } from 'lit';

export default css`
  :host {
    display: block;
    font-family: var(--font-family-base, 'Inter, Arial, sans-serif');
    --input-width: stretch;
    --input-min-width: 300px;
    --input-border: var(--sm-line, 1.5px) solid var(--border-strong);
    --input-bg: var(--surface-raised);
    --label-color: var(--on-surface-muted);
    --text-color: var(--on-surface-muted);
    --text-color-disabled: var(--on-surface-subtle);
  }

  .input-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-xs, 8px);
  }

  .label, ::slotted([slot="label"]) {
    width: 100%;
    font-size: var(--font-size-label);
    line-height: var(--line-height-label);
    font-weight: var(--font-weight-medium, 500);
    color: var(--label-color);
  }

  .input-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: var(--input-width, stretch);
    width: -webkit-fill-available;
    width: -moz-available;
    min-width: var(--input-min-width);
    box-sizing: border-box;
    border: var(--input-border);
    background: var(--input-bg);
    transition: border-color 0.2s ease;
  }

  :host([height="tiny"]) .input-container {
    height: var(--height-input-tiny);
    padding: calc(var(--spacing-xs, 6px) / 2) var(--spacing-xs, 8px);
    border-radius: var(--radius-sm, 8px);
  }

  :host([height="small"]) .input-container {
    height: var(--height-input-small);
    padding: calc(var(--spacing-xs, 8px) / 2) var(--spacing-xs, 10px);
    border-radius: var(--radius-sm, 8px);
  }

  :host([height="medium"]) .input-container {
    height: var(--height-input-medium);
    padding: calc(var(--spacing-xs, 8px) / 2) var(--spacing-xs, 12px);
    border-radius: var(--radius-sm, 8px);
  }

  :host([height="large"]) .input-container {
    height: var(--height-input-large);
    padding: var(--spacing-xs, 12px);
    border-radius: var(--radius-md, 12px);
  }

  :host([height="giant"]) .input-container {
    height: var(--height-input-giant);
    padding: var(--spacing-sm, 14px);
    border-radius: var(--radius-md, 12px);
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: var(--text-color);
    flex: none;
  }

  .icon.left {
    margin-right: var(--spacing-xs, 12px);
    margin-left: var(--spacing-xs, 12px);
  }

  .icon.right {
    margin-right: var(--spacing-xs, 12px);
    margin-left: var(--spacing-xs, 12px);
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    color: var(--text-color);
  }

  :host([height="tiny"]) input {
    font-size: var(--font-size-b4, 12px);
    line-height: var(--line-height-b4, 18px);
  }

  :host([height="small"]) input {
    font-size: var(--font-size-b3, 14px);
    line-height: var(--line-height-b3, 20px);
  }

  :host([height="medium"]) input {
    font-size: var(--font-size-b3, 14px);
    line-height: var(--line-height-b3, 20px);
  }

  :host([height="large"]) input {
    font-size: var(--font-size-b1, 16px);
    line-height: var(--line-height-b1, 24px);
  }

  :host([height="giant"]) input {
    font-size: var(--font-size-b1, 16px);
    line-height: var(--line-height-b1, 24px);
  }

  input::placeholder {
    color: var(--text-color);
  }

  .helper-text, ::slotted([slot="helper-text"]) {
    width: 100%;
    font-size: var(--font-size-b3, 14px);
    line-height: var(--line-height-b3, 20px);
    font-weight: var(--font-weight-regular, 400);
    color: var(--text-color);
  }

  :host([disabled]) .input-wrapper,
  :host([state="disabled"]) .input-wrapper {
    cursor: not-allowed;
    opacity: 0.7;
  }

  /* State-specific styles */
  :host([state="hover"]) .input-container {
    border-color: var(--color-secondary, #2594e3);
  }

  :host([state="focus"]) .input-container {
    border-color: var(--color-primary-dark, #001d68);
    box-shadow: var(--shadow-focus);
  }

  :host([state="focus"]) .helper-text,
  :host([state="focus"]) ::slotted([slot="helper-text"]) {
    color: var(--color-primary-dark, #001d68);
  }

  :host([state="disabled"]) .input-container {
    border-color: var(--text-color-disabled);
  }

  :host([state="disabled"]) input,
  :host([state="disabled"]) input::placeholder,
  :host([state="disabled"]) .label,
  :host([state="disabled"]) ::slotted([slot="label"]),
  :host([state="disabled"]) .helper-text,
  :host([state="disabled"]) ::slotted([slot="helper-text"]),
  :host([state="disabled"]) .icon {
    color: var(--text-color-disabled);
  }

  :host([state="success"]) .input-container {
    border-color: var(--color-success, #72b509);
  }

  :host([state="success"]) .helper-text,
  :host([state="success"]) ::slotted([slot="helper-text"]) {
    color: var(--color-success, #72b509);
  }

  :host([state="info"]) .input-container {
    border-color: var(--color-secondary, #2594e3);
  }

  :host([state="info"]) .helper-text,
  :host([state="info"]) ::slotted([slot="helper-text"]) {
    color: var(--color-secondary, #2594e3);
  }

  :host([state="warning"]) .input-container {
    border-color: var(--color-warning, #ff9505);
  }

  :host([state="warning"]) .helper-text,
  :host([state="warning"]) ::slotted([slot="helper-text"]) {
    color: var(--color-warning, #ff9505);
  }

  :host([state="error"]) .input-container {
    border-color: var(--color-error, #ff2b32);
  }

  :host([state="error"]) .helper-text,
  :host([state="error"]) ::slotted([slot="helper-text"]) {
    color: var(--color-error, #ff2b32);
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .input-container {
      width: 100%;
      /* 200px is wider than the content box of some drawer panels at 360px,
         which made the container itself the thing that overflowed */
      min-width: 0;
    }

    /* iOS Safari zooms the whole viewport when a focused input's font-size is
       under 16px, and there is no way back short of a manual pinch. Every
       tiny/small/medium input in Studio is 12-14px. */
    :host([height="tiny"]) input,
    :host([height="small"]) input,
    :host([height="medium"]) input,
    input {
      font-size: max(16px, var(--font-size-b3, 14px));
    }
  }
`;